import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  CancelarInscripcion,
  GetInscriptos,
  InscripcionColeccion,
  Inscripto,
} from "../inscripciones";

// ══════════════════════════════════════════════════════════════
// Lista de inscriptos para el panel de Admin (agregado 11/10/2026)
//
// Se abre al tocar un evento o una noticia en las tablas de Admin.
// Permite copiar los emails, descargar un CSV (se abre en Excel) y
// dar de baja a alguien.
// ══════════════════════════════════════════════════════════════

export interface InscriptosTarget {
  coleccion: InscripcionColeccion;
  id: string;
  titulo: string;
  /** Si el botón "Inscribirme" está activado en ese evento/noticia. */
  abierta: boolean;
}

interface InscriptosModalProps {
  target: InscriptosTarget | null;
  onClose: () => void;
}

const formatFecha = (fecha: Date | null) =>
  fecha ? dayjs(fecha).format("DD/MM/YYYY HH:mm") : "-";

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const InscriptosModal: React.FC<InscriptosModalProps> = ({
  target,
  onClose,
}) => {
  const [inscriptos, setInscriptos] = useState<Inscripto[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    try {
      setInscriptos(await GetInscriptos(target.coleccion, target.id));
    } catch {
      message.error("No se pudo cargar la lista de inscriptos.");
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    if (target) {
      void load();
    } else {
      setInscriptos([]);
    }
  }, [target, load]);

  const tipo = target?.coleccion === "Eventos" ? "evento" : "noticia";

  const handleCopyEmails = async () => {
    const emails = inscriptos.map((i) => i.Email).filter(Boolean);
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      message.success(`Copiaste ${emails.length} emails.`);
    } catch {
      message.error("No se pudieron copiar los emails.");
    }
  };

  const handleDownloadCsv = () => {
    if (!target) return;
    const rows = [
      ["Nombre", "Email", "Fecha de inscripción"],
      ...inscriptos.map((i) => [i.Nombre, i.Email, formatFecha(i.Fecha)]),
    ];
    // ";" y BOM para que Excel en español lo abra con columnas y acentos.
    const csv =
      "\uFEFF" + rows.map((row) => row.map(csvCell).join(";")).join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const slug =
      target.titulo
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40) || target.id;
    const link = document.createElement("a");
    link.href = url;
    link.download = `inscriptos-${slug}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRemove = async (record: Inscripto) => {
    if (!target) return;
    try {
      await CancelarInscripcion(target.coleccion, target.id, record.Uid);
      message.success(`${record.Nombre || record.Email} ya no está inscripto.`);
      void load();
    } catch {
      message.error("No se pudo quitar la inscripción.");
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: unknown, __: Inscripto, index: number) => index + 1,
    },
    {
      title: "Nombre",
      dataIndex: "Nombre",
      key: "Nombre",
      render: (value: string) => value || "-",
    },
    { title: "Email", dataIndex: "Email", key: "Email" },
    {
      title: "Inscripción",
      dataIndex: "Fecha",
      key: "Fecha",
      render: (value: Date | null) => formatFecha(value),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: unknown, record: Inscripto) => (
        <Popconfirm
          title="¿Quitar a esta persona de la lista?"
          okText="Quitar"
          okButtonProps={{ danger: true }}
          cancelText="Cancelar"
          onConfirm={() => void handleRemove(record)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      open={Boolean(target)}
      onCancel={onClose}
      width={760}
      title={
        <Space wrap>
          <span>Inscriptos</span>
          <Tag color="purple">{inscriptos.length}</Tag>
        </Space>
      }
      footer={[
        <Button key="cerrar" type="primary" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
    >
      {target ? (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text strong style={{ color: "var(--dashboard-header-blue)" }}>
            {target.titulo}
          </Typography.Text>

          {!target.abierta ? (
            <Alert
              type="info"
              showIcon
              message={`El botón "Inscribirme" está desactivado en esta ${tipo}. Activalo desde "Editar" para que la gente pueda anotarse.`}
            />
          ) : null}

          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void load()}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => void handleCopyEmails()}
              disabled={!inscriptos.length}
            >
              Copiar emails
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCsv}
              disabled={!inscriptos.length}
            >
              Descargar CSV
            </Button>
          </Space>

          <Table
            rowKey="Uid"
            size="small"
            columns={columns as any}
            dataSource={inscriptos}
            loading={loading}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            scroll={{ x: true }}
            locale={{ emptyText: `Todavía no se inscribió nadie en esta ${tipo}.` }}
          />
        </Space>
      ) : null}
    </Modal>
  );
};

export default InscriptosModal;
